import { useEffect, useMemo } from "react";
import { useInterval } from "usehooks-ts";
import { create } from "zustand";

interface AffectedComponentResponse {
  id: string;
  name: string;
  group_name?: string;
}

interface MaintenanceResponse {
  id: string;
  name: string;
  status: string;
  url: string;
  last_update_at: string;
  last_update_message: string;
  affected_components: AffectedComponentResponse[];
}

interface InProgressMaintenanceResponse extends MaintenanceResponse {
  started_at: string;
  scheduled_end_at: string;
}

interface ScheduledMaintenanceResponse extends MaintenanceResponse {
  starts_at: string;
  ends_at: string;
}

type IncidentStatus = "identified" | "investigating" | "monitoring";

interface IncidentResponse {
  id: string;
  name: string;
  status: IncidentStatus;
  url: string;
  last_update_at: string;
  last_update_message: string;
  current_worst_impact: string;
  affected_components: AffectedComponentResponse[];
}

interface SummaryResponse {
  page_title: string;
  page_url: string;
  ongoing_incidents: IncidentResponse[];
  in_progress_maintenances: InProgressMaintenanceResponse[];
  scheduled_maintenances: ScheduledMaintenanceResponse[];
}

export interface Maintenance {
  id: string;
  name: string;
  url: string;
  /** Whether it has started, or is still scheduled. */
  isUnderWay: boolean;
  /** A scheduled maintenance's planned start, as an ISO timestamp. */
  startsAt?: string;
  /** A maintenance's planned end, as an ISO timestamp. */
  endsAt?: string;
}

interface IncidentSummaryState {
  summary: SummaryResponse | null;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  /** When the last read finished, whether it succeeded or not. */
  fetchedAt: number;
  fetchSummary: () => Promise<void>;
}

const URL = "https://status.layer.ai/api/v1/summary";

/** How long a read of the summary is used before the next, so an open tab sees a maintenance published or started later. */
const REFRESH_MS = 5 * 60 * 1000;

/** How often each consumer asks whether the summary is due a refresh. */
const CHECK_MS = 60 * 1000;

/** A scheduled maintenance is announced this long before it starts, and not earlier. */
export const MAINTENANCE_LEAD_MS = 7 * 24 * 60 * 60 * 1000;

// This store prevents unnecessary data refetching, especially in cases like opening and closing the menu, which triggers a re-render.
// A failed read keeps the last summary.
const useIncidentSummaryStore = create<IncidentSummaryState>((set, get) => ({
  summary: null,
  error: null,
  isLoading: true,
  isFetching: false,
  fetchedAt: 0,
  fetchSummary: async () => {
    const { isFetching, fetchedAt } = get();
    if (isFetching || Date.now() - fetchedAt < REFRESH_MS) {
      return;
    }
    set({ isFetching: true });
    try {
      const response = await fetch(URL);
      if (!response.ok) {
        throw new Error(
          `Status page summary request failed with ${response.status}`,
        );
      }
      set({ summary: await response.json(), error: null });
    } catch (e) {
      console.error("Failed to fetch incident summary", e);
      const error = e instanceof Error ? e : new Error("Unknown error");
      set({ error });
    } finally {
      set({ isLoading: false, isFetching: false, fetchedAt: Date.now() });
    }
  },
}));

const useIncidentSummary = () => {
  const { summary, isLoading, fetchSummary } = useIncidentSummaryStore();

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  useInterval(fetchSummary, CHECK_MS);

  return { summary, isLoading };
};

const DEFAULT_STATUSES: IncidentStatus[] = ["identified", "investigating"];

export const useIncidents = (statuses = DEFAULT_STATUSES) => {
  const { summary, isLoading } = useIncidentSummary();

  const incidents = useMemo(() => {
    return summary?.ongoing_incidents.filter((incident) =>
      statuses.includes(incident.status),
    );
  }, [summary, statuses]);

  return { incidents, isLoading };
};

const timestamp = (value: unknown): string | undefined =>
  typeof value === "string" && !Number.isNaN(Date.parse(value))
    ? value
    : undefined;

/**
 * A scheduled maintenance is announced from the lead time before its start until its planned
 * end. Past its end it is over, even though the status page keeps listing it as scheduled until
 * someone starts or completes it. A time that does not parse never hides a maintenance.
 */
const isAnnounced = ({ startsAt, endsAt }: Maintenance, now: number) => {
  const isWithinLeadTime =
    startsAt === undefined || Date.parse(startsAt) - now <= MAINTENANCE_LEAD_MS;
  const hasEnded = endsAt !== undefined && Date.parse(endsAt) <= now;

  return isWithinLeadTime && !hasEnded;
};

const toMaintenances = (summary: SummaryResponse): Maintenance[] => {
  const now = Date.now();
  return [
    ...(summary.in_progress_maintenances ?? []).map(
      ({ id, name, url, scheduled_end_at }) => ({
        id,
        name,
        url,
        isUnderWay: true,
        endsAt: timestamp(scheduled_end_at),
      }),
    ),
    ...(summary.scheduled_maintenances ?? [])
      .map(({ id, name, url, starts_at, ends_at }) => ({
        id,
        name,
        url,
        isUnderWay: false,
        startsAt: timestamp(starts_at),
        endsAt: timestamp(ends_at),
      }))
      .filter((maintenance) => isAnnounced(maintenance, now)),
  ];
};

/** Maintenances under way, then those starting within the lead time and not yet past their end. */
export const useMaintenances = () => {
  const { summary, isLoading } = useIncidentSummary();

  const maintenances = useMemo(
    () => summary && toMaintenances(summary),
    [summary],
  );

  return { maintenances, isLoading };
};
